import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { QuizForm, type CourseSelectOption } from '@/components/quizzes/QuizForm'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useCourses } from '@/hooks/useCourses'
import { useQuiz, useUpdateQuiz } from '@/hooks/useQuizzes'
import { paths } from '@/routes/path'
import BackButton from '@/components/ui/BackButton'

export function QuizEditPage() {
  const { quizId } = useParams<{ quizId: string }>()
  const navigate = useNavigate()

  const { data: quiz, isLoading: isQuizLoading, error: quizError } = useQuiz(quizId)
  const { data: coursesData, isLoading: isCoursesLoading } = useCourses({ limit: 200 })
  const updateQuiz = useUpdateQuiz(quizId)

  const courseOptions = useMemo<CourseSelectOption[]>(() => {
    const items = coursesData?.data ?? []
    const base = Array.isArray(items)
      ? items
          .map((course: any) => ({
            id: String(course.id),
            label: course.title ?? course.name ?? 'Untitled course',
            subtitle: course.code ?? undefined,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      : []

    if (quiz && !base.some((option) => option.id === quiz.courseId)) {
      base.push({
        id: quiz.courseId,
        label: 'Current course',
        subtitle: `ID: ${quiz.courseId}`,
      })
    }

    return base
  }, [coursesData, quiz])

  if (!quizId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Invalid quiz</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not identify the quiz you want to edit.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isCoursesLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (quizError || !quiz) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Quiz not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              The quiz might have been deleted or you may not have permission to edit it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const handleSubmit = async (payload: any) => {
    await updateQuiz.mutateAsync({ ...payload, id: quizId })
    toast.success('Quiz updated successfully.')
    navigate(paths.app.quizzes.detail.build(quizId))
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">
            <div className="flex items-center gap-2">
              <BackButton/>
              <span>Edit quiz</span>
            </div>
          </CardTitle>
          <CardDescription>
            Update the quiz details. Changes are saved immediately after you submit.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <QuizForm
            courses={courseOptions}
            initialValues={{
              courseId: quiz.courseId,
              title: quiz.title,
              description: quiz.description ?? '',
              isActive: quiz.isActive,
              allowDevices: quiz.allowDevices ?? [],
              requireProctoring: quiz.requireProctoring ?? false,
              maxAttempts: quiz.maxAttempts ?? null,
              timeLimitSeconds: quiz.timeLimitSeconds ?? null,
              dueDate: quiz.dueDate ?? null,
              slug: quiz.slug ?? undefined,
            }}
            onSubmit={handleSubmit}
            isSubmitting={updateQuiz.isPending}
            submitLabel="Save changes"
          />
        </CardContent>
      </Card>
    </div>
  )
}
